import {
  Alert,
  Anchor,
  Badge,
  Box,
  Breadcrumbs,
  Button,
  Container,
  Divider,
  Group,
  NumberInput,
  Paper,
  RingProgress,
  SimpleGrid,
  Slider,
  Stack,
  Table,
  Text,
  ThemeIcon,
  Title,
} from "@mantine/core";
import {
  IconArrowLeftBar,
  IconBook2,
  IconCalculator,
  IconChartBar,
  IconChecklist,
  IconFileText,
  IconInfoCircle,
  IconTargetArrow,
} from "@tabler/icons-react";
import { FormEvent, ReactNode, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import GetHelpForm from "../Shared/GetHelpForm";
import Seo from "../../seo/Seo";
import {
  createBreadcrumbSchema,
  createFaqSchema,
  createOrganizationSchema,
} from "../../seo/utils";
import "./ApushScoreCalculatorPage.css";

type ScoreKey = "mcq" | "saq" | "dbq" | "leq";
type ScoreValue = number | "";
type ScoreInputs = Record<ScoreKey, ScoreValue>;
type ScoreErrors = Partial<Record<ScoreKey, string>>;

type SectionConfig = {
  key: ScoreKey;
  label: string;
  helper: string;
  max: number;
  weight: number;
  icon: JSX.Element;
};

const TOOL_PATH = "/tools/apush-score-calculator";
const TOOLS_PATH = "/tools";
const seoTitle =
  "APUSH Score Calculator | Estimate Your AP U.S. History Score";
const seoDescription =
  "Use SubjectBuddy's APUSH score calculator to estimate your AP U.S. History exam score from MCQ, SAQ, DBQ, and LEQ practice scores.";

const sectionConfigs: SectionConfig[] = [
  {
    key: "mcq",
    label: "Multiple Choice Score",
    helper: "Enter your MCQ score out of 55.",
    max: 55,
    weight: 40,
    icon: <IconChecklist size={20} />,
  },
  {
    key: "saq",
    label: "Short Answer Score",
    helper: "Enter your SAQ score out of 9.",
    max: 9,
    weight: 20,
    icon: <IconBook2 size={20} />,
  },
  {
    key: "dbq",
    label: "DBQ Score",
    helper: "Enter your Document-Based Question score out of 7.",
    max: 7,
    weight: 25,
    icon: <IconFileText size={20} />,
  },
  {
    key: "leq",
    label: "LEQ Score",
    helper: "Enter your Long Essay Question score out of 6.",
    max: 6,
    weight: 15,
    icon: <IconChartBar size={20} />,
  },
];

const scoreMeanings: Record<number, string> = {
  5: "Excellent APUSH performance",
  4: "Strong APUSH performance",
  3: "Generally considered qualified",
  2: "May need improvement",
  1: "Needs significant improvement",
};

const conversionRows = [
  ["80-100%", "5", "Extremely well qualified"],
  ["67-79%", "4", "Well qualified"],
  ["55-66%", "3", "Qualified"],
  ["45-54%", "2", "Possibly qualified"],
  ["0-44%", "1", "No recommendation"],
];

const relatedTools = [
  {
    title: "AP Bio Score Calculator",
    path: "/tools/ap-bio-score-calculator",
    description:
      "Use the AP Bio score calculator to estimate your AP Biology practice exam score.",
  },
  {
    title: "AP Chem Score Calculator",
    path: "/tools/ap-chem-score-calculator",
    description:
      "Use the AP Chem score calculator to estimate your AP Chemistry practice exam score.",
  },
  {
    title: "AP Lang Score Calculator",
    path: "/tools/ap-lang-score-calculator",
    description:
      "Use the AP Lang score calculator to estimate your AP English Language and Composition score.",
  },
  {
    title: "AP CSP Score Calculator",
    path: "/tools/ap-csp-score-calculator",
    description:
      "Use the AP CSP score calculator to estimate your AP Computer Science Principles score.",
  },
  {
    title: "AP Psych Score Calculator",
    path: "/tools/ap-psych-score-calculator",
    description:
      "Use the AP Psych score calculator to estimate your AP Psychology exam score.",
  },
  {
    title: "AP Human Geography Score Calculator",
    path: "/tools/ap-human-geography-score-calculator",
    description:
      "Use the AP Human Geography score calculator to estimate your AP Human Geography exam score.",
  },
  {
    title: "AP Grading Scale",
    path: "/tools/ap-grading-scale",
    description:
      "Use the AP Grading Scale guide to understand AP score meanings from 1 to 5.",
  },
];

const faqs = [
  {
    question: "Is this APUSH score calculator accurate?",
    answer:
      "This APUSH score calculator gives an estimated score based on common AP U.S. History exam weighting. It is useful for practice, but official score conversions may vary each year.",
  },
  {
    question: "Can I use this as an APUSH exam score calculator?",
    answer:
      "Yes. You can use this as an APUSH exam score calculator by entering your MCQ, SAQ, DBQ, and LEQ scores from a practice exam.",
  },
  {
    question: "Is this APUSH test score calculator useful for practice tests?",
    answer:
      "Yes. This APUSH test score calculator is designed for students who want to estimate their APUSH score after completing a practice test.",
  },
  {
    question: "How does the AP grading scale work?",
    answer:
      "The AP grading scale reports scores from 1 to 5. A score of 3 is usually considered qualified, while 4 and 5 are stronger scores. Exact conversions may vary by exam year.",
  },
  {
    question: "What is a good APUSH score?",
    answer:
      "A score of 3 is commonly considered qualified, while a 4 or 5 is a stronger result. Many students use an APUSH calculator during practice to understand whether they are on track.",
  },
  {
    question: "Where can I find other AP score calculators?",
    answer:
      "You can also use our AP Lang score calculator, AP CSP score calculator, AP Psych score calculator, and AP Human Geography score calculator to estimate scores for other AP exams.",
  },
  {
    question: "How many multiple-choice questions are on the APUSH exam?",
    answer:
      "The AP U.S. History exam has 55 multiple-choice questions, and this section counts for 40% of the total exam score.",
  },
];

function getNumericScore(value: ScoreValue) {
  return typeof value === "number" && Number.isFinite(value) ? value : 0;
}

function getEstimatedScore(weightedPercentage: number) {
  if (weightedPercentage >= 80) {
    return 5;
  }

  if (weightedPercentage >= 67) {
    return 4;
  }

  if (weightedPercentage >= 55) {
    return 3;
  }

  if (weightedPercentage >= 45) {
    return 2;
  }

  return 1;
}

function calculateResult(scores: ScoreInputs) {
  const weightedPercentage = sectionConfigs.reduce((total, section) => {
    const rawScore = getNumericScore(scores[section.key]);
    return total + (rawScore / section.max) * section.weight;
  }, 0);
  const estimatedScore = getEstimatedScore(weightedPercentage);

  return {
    weightedPercentage,
    estimatedScore,
    meaning: scoreMeanings[estimatedScore],
    sections: sectionConfigs.map((section) => {
      const rawScore = getNumericScore(scores[section.key]);
      const percentage = (rawScore / section.max) * 100;
      const weightedScore = (rawScore / section.max) * section.weight;

      return {
        ...section,
        rawScore,
        percentage,
        weightedScore,
      };
    }),
  };
}

function validateScores(scores: ScoreInputs) {
  const nextErrors: ScoreErrors = {};

  sectionConfigs.forEach((section) => {
    const value = scores[section.key];

    if (value === "" || value === null || value === undefined) {
      nextErrors[section.key] = `${section.label.replace(" Score", "")} score is required.`;
      return;
    }

    if (typeof value !== "number" || Number.isNaN(value)) {
      nextErrors[section.key] = `${section.label.replace(" Score", "")} score must be a number.`;
      return;
    }

    if (value < 0 || value > section.max) {
      nextErrors[section.key] = `${section.label.replace(" Score", "")} score must be between 0 and ${section.max}.`;
    }
  });

  return nextErrors;
}

function ApushScoreCalculatorPage() {
  const navigate = useNavigate();
  const [scores, setScores] = useState<ScoreInputs>({
    mcq: 36,
    saq: 5,
    dbq: 5,
    leq: 4,
  });
  const [errors, setErrors] = useState<ScoreErrors>({});
  const [result, setResult] = useState<ReturnType<typeof calculateResult> | null>(
    null,
  );
  const previewResult = useMemo(() => calculateResult(scores), [scores]);

  function updateScore(key: ScoreKey, value: string | number) {
    setScores((currentScores) => ({
      ...currentScores,
      [key]: value === "" ? "" : Number(value),
    }));
    setErrors((currentErrors) => ({
      ...currentErrors,
      [key]: undefined,
    }));
  }

  function handleSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    const nextErrors = validateScores(scores);
    setErrors(nextErrors);

    if (Object.keys(nextErrors).length) {
      setResult(null);
      return;
    }

    setResult(calculateResult(scores));
  }

  const softwareApplicationSchema = {
    "@context": "https://schema.org",
    "@type": "WebApplication",
    name: "APUSH Score Calculator",
    applicationCategory: "EducationalApplication",
    operatingSystem: "Web",
    description:
      "A free APUSH score calculator that helps students estimate their AP U.S. History score from practice test results.",
    offers: {
      "@type": "Offer",
      price: "0",
      priceCurrency: "USD",
    },
  };

  return (
    <Container fluid className="apush-page" px={0}>
      <Seo
        title={seoTitle}
        description={seoDescription}
        path={TOOL_PATH}
        ogTitle="APUSH Score Calculator"
        ogDescription="Estimate your AP U.S. History score using MCQ, SAQ, DBQ, and LEQ practice results."
        keywords={[
          "apush score calculator",
          "apush exam score calculator",
          "apush test score calculator",
          "ap score calculator apush",
          "ap grading scale",
          "apush score calculator 2026",
          "ap us history score calculator",
          "apush exam score",
          "how to calculate apush score",
          "apush calculator",
        ]}
        schema={[
          createOrganizationSchema(),
          createBreadcrumbSchema([
            { name: "Home", path: "/" },
            { name: "Tools", path: TOOLS_PATH },
            { name: "APUSH Score Calculator", path: TOOL_PATH },
          ]),
          createFaqSchema(faqs),
          softwareApplicationSchema,
        ]}
      />

      <Box className="apush-hero">
        <Container size="xl">
          <Stack gap="lg">
            <Breadcrumbs separator="/">
              <Anchor component={Link} to="/">
                Home
              </Anchor>
              <Anchor component={Link} to={TOOLS_PATH}>
                Tools
              </Anchor>
              <Text>APUSH Score Calculator</Text>
            </Breadcrumbs>

            <Button
              color="blue"
              variant="subtle"
              leftSection={<IconArrowLeftBar />}
              onClick={() => navigate(TOOLS_PATH)}
              className="apush-back-button"
            >
              Back to Tools
            </Button>

            <SimpleGrid cols={{ base: 1, lg: 2 }} spacing={36}>
              <Stack gap="lg" justify="center">
                <Group gap="sm">
                  <Badge color="green" size="lg" radius="sm">
                    AP U.S. History
                  </Badge>
                  <Badge color="blue" size="lg" radius="sm" variant="light">
                    Free Student Tool
                  </Badge>
                </Group>
                <Title order={1} className="apush-title">
                  APUSH Score Calculator
                </Title>
                <Text size="lg" c="dimmed" className="apush-hero-copy">
                  Use this APUSH score calculator to estimate your AP U.S.
                  History score before the official result. Enter your
                  multiple-choice, short-answer, DBQ, and LEQ scores to get a
                  quick prediction of your AP score from 1 to 5.
                </Text>
                <Alert
                  icon={<IconInfoCircle size={18} />}
                  color="blue"
                  radius="lg"
                  variant="light"
                >
                  This APUSH calculator gives an estimated score based on common
                  AP U.S. History exam weighting. Official AP score conversions
                  may vary each year.
                </Alert>
              </Stack>

              <Paper className="apush-result-panel" radius="xl" p="xl">
                <Stack align="center" gap="md">
                  <Text size="sm" fw={800} tt="uppercase" c="dimmed">
                    Current Practice Estimate
                  </Text>
                  <RingProgress
                    size={220}
                    thickness={18}
                    roundCaps
                    sections={[
                      {
                        value: previewResult.weightedPercentage,
                        color:
                          previewResult.estimatedScore >= 4 ? "green" : "blue",
                      },
                    ]}
                    label={
                      <Stack align="center" gap={0}>
                        <Text fw={900} className="apush-score-number">
                          {previewResult.estimatedScore}
                        </Text>
                        <Text size="xs" c="dimmed">
                          estimated AP score
                        </Text>
                      </Stack>
                    }
                  />
                  <Text fw={800}>{previewResult.meaning}</Text>
                  <Text c="dimmed" ta="center" size="sm">
                    Weighted percentage:{" "}
                    {previewResult.weightedPercentage.toFixed(1)}%
                  </Text>
                </Stack>
              </Paper>
            </SimpleGrid>
          </Stack>
        </Container>
      </Box>

      <Container size="xl" py={48}>
        <div className="apush-layout">
          <main className="apush-main">
            <section aria-labelledby="apush-calculator-heading">
              <Paper
                id="apush-calculator-form"
                withBorder
                radius="xl"
                p="xl"
                className="apush-tool-card"
              >
                <form onSubmit={handleSubmit} noValidate>
                  <Stack gap="lg">
                    <Stack gap={4}>
                      <Title id="apush-calculator-heading" order={2}>
                        Estimate Your APUSH Score
                      </Title>
                      <Text c="dimmed">
                        Enter your practice exam scores below. The calculator
                        uses MCQ 40%, SAQ 20%, DBQ 25%, and LEQ 15%.
                      </Text>
                    </Stack>

                    <SimpleGrid cols={{ base: 1, md: 2 }} spacing="lg">
                      {sectionConfigs.map((section) => (
                        <Paper
                          key={section.key}
                          withBorder
                          radius="lg"
                          p="lg"
                          className="apush-input-card"
                        >
                          <Stack gap="sm">
                            <Group justify="space-between" align="flex-start">
                              <Group gap="sm" wrap="nowrap">
                                <ThemeIcon
                                  color="green"
                                  variant="light"
                                  radius="xl"
                                >
                                  {section.icon}
                                </ThemeIcon>
                                <Stack gap={0}>
                                  <Text
                                    component="label"
                                    htmlFor={`apush-${section.key}`}
                                    fw={800}
                                  >
                                    {section.label}
                                  </Text>
                                  <Text size="sm" c="dimmed">
                                    {section.helper}
                                  </Text>
                                </Stack>
                              </Group>
                              <Badge color="blue" variant="light" radius="sm">
                                {section.weight}%
                              </Badge>
                            </Group>

                            <NumberInput
                              id={`apush-${section.key}`}
                              value={scores[section.key]}
                              min={0}
                              max={section.max}
                              clampBehavior="strict"
                              onChange={(value) =>
                                updateScore(section.key, value)
                              }
                              error={errors[section.key]}
                              rightSection={
                                <Text size="xs" c="dimmed">
                                  /{section.max}
                                </Text>
                              }
                              aria-label={section.label}
                              required
                            />

                            <Slider
                              value={getNumericScore(scores[section.key])}
                              min={0}
                              max={section.max}
                              step={1}
                              marks={[
                                { value: 0, label: "0" },
                                { value: section.max, label: `${section.max}` },
                              ]}
                              color="green"
                              aria-label={`${section.label} slider`}
                              onChange={(value) =>
                                updateScore(section.key, value)
                              }
                              className="apush-score-slider"
                            />
                          </Stack>
                        </Paper>
                      ))}
                    </SimpleGrid>

                    <Button
                      type="submit"
                      size="lg"
                      radius="lg"
                      leftSection={<IconCalculator />}
                    >
                      Calculate APUSH Score
                    </Button>
                  </Stack>
                </form>
              </Paper>
            </section>

            {result ? (
              <section aria-labelledby="apush-result-heading">
                <Paper withBorder radius="xl" p="xl" className="apush-result-card">
                  <Stack gap="lg">
                    <Group justify="space-between" align="center">
                      <Stack gap={2}>
                        <Text size="sm" c="dimmed" fw={800} tt="uppercase">
                          Result
                        </Text>
                        <Title id="apush-result-heading" order={2}>
                          Estimated AP Score: {result.estimatedScore}
                        </Title>
                      </Stack>
                      <ThemeIcon size={58} radius="xl" color="green">
                        <IconTargetArrow size={30} />
                      </ThemeIcon>
                    </Group>
                    <SimpleGrid cols={{ base: 1, sm: 2 }} spacing="md">
                      <Paper radius="lg" p="md" className="apush-mini-stat">
                        <Text fw={900} size="xl">
                          {result.weightedPercentage.toFixed(1)}%
                        </Text>
                        <Text size="sm" c="dimmed">
                          Estimated Weighted Percentage
                        </Text>
                      </Paper>
                      <Paper radius="lg" p="md" className="apush-mini-stat">
                        <Text fw={900} size="xl">
                          {result.meaning}
                        </Text>
                        <Text size="sm" c="dimmed">
                          Score meaning
                        </Text>
                      </Paper>
                    </SimpleGrid>
                    <Divider />
                    <Stack gap="md">
                      <Title order={3}>Section Breakdown</Title>
                      {result.sections.map((section) => (
                        <Box key={section.key}>
                          <Group justify="space-between" mb={6}>
                            <Text fw={700}>{section.label}</Text>
                            <Text size="sm" c="dimmed">
                              {section.weightedScore.toFixed(1)} /{" "}
                              {section.weight}
                            </Text>
                          </Group>
                          <Slider
                            value={section.percentage}
                            min={0}
                            max={100}
                            step={1}
                            color={section.weight >= 25 ? "green" : "blue"}
                            label={(value) => `${value}%`}
                            disabled
                          />
                        </Box>
                      ))}
                    </Stack>
                  </Stack>
                </Paper>
              </section>
            ) : null}

            <article className="apush-content">
              <SeoSection title="What Is an APUSH Score Calculator?">
                <Text>
                  An APUSH score calculator is a student tool that estimates
                  your AP U.S. History exam score using your practice test
                  results. Instead of guessing your final AP score, you can
                  enter your MCQ, SAQ, DBQ, and LEQ marks to see an estimated
                  score from 1 to 5.
                </Text>
                <Text>
                  This AP U.S. History score calculator is useful when you want
                  a quick score estimate before the official AP result.
                </Text>
              </SeoSection>

              <SeoSection title="How the APUSH Exam Is Scored">
                <Text>
                  The College Board AP U.S. History exam format includes
                  multiple-choice questions, short-answer questions, a
                  document-based question, and a long essay question. The
                  multiple-choice section includes 55 questions in 55 minutes
                  and counts for 40% of the exam score. Short-answer questions
                  include 3 questions in 40 minutes and count for 20%, the DBQ
                  counts for 25%, and the LEQ counts for 15%.
                </Text>
                <ResponsiveTable
                  headers={["Section", "Raw Score", "Exam Weight"]}
                  rows={[
                    ["Multiple Choice", "55 questions", "40%"],
                    ["Short Answer", "9 points", "20%"],
                    ["DBQ", "7 points", "25%"],
                    ["LEQ", "6 points", "15%"],
                  ]}
                />
              </SeoSection>

              <SeoSection title="APUSH Exam Score Calculator Formula">
                <Text>
                  This APUSH exam score calculator converts each section into a
                  percentage, applies the APUSH exam weighting, and then
                  estimates your final AP score from 1 to 5.
                </Text>
                <Paper withBorder radius="lg" p="lg" className="apush-formula">
                  <Text fw={800}>Weighted Score =</Text>
                  <Text>
                    (MCQ percentage x 40) + (SAQ percentage x 20) + (DBQ
                    percentage x 25) + (LEQ percentage x 15)
                  </Text>
                </Paper>
              </SeoSection>

              <SeoSection title="APUSH Test Score Calculator for Practice Exams">
                <Text>
                  If you have just completed a practice test, this APUSH test
                  score calculator can help you check whether your current
                  performance is close to a 3, 4, or 5. It is especially useful
                  when you are preparing for the APUSH exam and want to track
                  improvement over time.
                </Text>
              </SeoSection>

              <SeoSection title="AP Score Calculator APUSH Guide">
                <Text>
                  Students searching for an AP score calculator APUSH tool can
                  use this page to estimate their score using the main AP U.S.
                  History exam sections. The calculator is designed for practice
                  and planning, not for official score reporting.
                </Text>
              </SeoSection>

              <SeoSection title="APUSH Score Conversion Table">
                <ResponsiveTable
                  headers={[
                    "Estimated Percentage",
                    "Predicted AP Score",
                    "Meaning",
                  ]}
                  rows={conversionRows}
                />
                <Text>
                  The AP grading scale reports final scores from 1 to 5, but
                  the exact raw-score conversion can change by exam year. This
                  table is only an estimated guide for practice.
                </Text>
              </SeoSection>

              <SeoSection title="How Many Points Do You Need to Get a 5 on APUSH?">
                <Text>
                  To get a 5 on APUSH, students usually need strong performance
                  across all sections, especially the multiple-choice section
                  and DBQ. A high MCQ score, strong evidence use in the DBQ, and
                  a clear LEQ argument can improve your estimated APUSH score.
                </Text>
              </SeoSection>

              <SeoSection title="How Many Points Do You Need to Get a 3 on APUSH?">
                <Text>
                  A score of 3 usually requires a balanced performance across
                  MCQ, SAQ, DBQ, and LEQ. This APUSH score calculator can help
                  you test different score combinations and understand which
                  sections need improvement.
                </Text>
              </SeoSection>

              <SeoSection title="APUSH Exam Score Calculator vs AP Grading Scale">
                <Text>
                  An APUSH exam score calculator estimates your score using
                  practice test results, while the AP grading scale shows the
                  final AP score range from 1 to 5. Since official AP score
                  conversions may change each year, this calculator should be
                  used as a helpful estimate rather than an official result.
                </Text>
              </SeoSection>

              <SeoSection title="Related AP Score Calculators">
                <SimpleGrid cols={{ base: 1, md: 2 }} spacing="md">
                  {relatedTools.map((tool) => (
                    <Paper
                      key={tool.path}
                      component={Link}
                      to={tool.path}
                      withBorder
                      radius="lg"
                      p="lg"
                      className="apush-related-card"
                    >
                      <Stack gap="xs">
                        <Text fw={800} c="green">
                          {tool.title}
                        </Text>
                        <Text size="sm" c="dimmed">
                          {tool.description}
                        </Text>
                      </Stack>
                    </Paper>
                  ))}
                </SimpleGrid>
              </SeoSection>

              <SeoSection title="Related Student Support">
                <SimpleGrid cols={{ base: 1, md: 2 }} spacing="md">
                  <Paper
                    component={Link}
                    to="/history-assignment-help/"
                    withBorder
                    radius="lg"
                    p="lg"
                    className="apush-related-card"
                  >
                    <Stack gap="xs">
                      <Text fw={800} c="green">
                        History Assignment Help
                      </Text>
                      <Text size="sm" c="dimmed">
                        Get support with U.S. history themes, evidence, DBQ
                        planning, and LEQ structure.
                      </Text>
                    </Stack>
                  </Paper>
                  <Paper
                    component={Link}
                    to="/assignment-help"
                    withBorder
                    radius="lg"
                    p="lg"
                    className="apush-related-card"
                  >
                    <Stack gap="xs">
                      <Text fw={800} c="green">
                        Assignment Help
                      </Text>
                      <Text size="sm" c="dimmed">
                        Use SubjectBuddy support to turn practice score results
                        into a clearer revision plan.
                      </Text>
                    </Stack>
                  </Paper>
                </SimpleGrid>
              </SeoSection>

              <SeoSection title="APUSH Score Calculator FAQs">
                <Stack gap="md">
                  {faqs.map((faq) => (
                    <Paper key={faq.question} withBorder radius="lg" p="lg">
                      <Title order={3}>{faq.question}</Title>
                      <Text c="dimmed">{faq.answer}</Text>
                    </Paper>
                  ))}
                </Stack>
              </SeoSection>
            </article>
          </main>

          <aside className="apush-sidebar" aria-label="Study support form">
            <div className="apush-sidebar-sticky">
              <Stack gap="md">
                <Paper withBorder radius="xl" p="lg">
                  <Stack gap="xs">
                    <Text size="sm" tt="uppercase" fw={800} c="green">
                      Study support
                    </Text>
                    <Title order={3}>Need Help Preparing?</Title>
                    <Text c="dimmed" size="sm">
                      Share your APUSH goals, practice score, or deadline and
                      we will help you plan the next study step.
                    </Text>
                  </Stack>
                </Paper>
                <Paper withBorder radius="xl" p={0} className="apush-help-form">
                  <GetHelpForm formSize="sm" flatForm />
                </Paper>
              </Stack>
            </div>
          </aside>
        </div>
      </Container>
    </Container>
  );
}

function SeoSection({
  title,
  children,
}: {
  title: string;
  children: ReactNode;
}) {
  return (
    <section className="apush-seo-section">
      <Stack gap="md">
        <Title order={2}>{title}</Title>
        {children}
      </Stack>
    </section>
  );
}

function ResponsiveTable({
  headers,
  rows,
}: {
  headers: string[];
  rows: string[][];
}) {
  return (
    <Box className="apush-table-wrap">
      <Table striped highlightOnHover withTableBorder withColumnBorders>
        <Table.Thead>
          <Table.Tr>
            {headers.map((header) => (
              <Table.Th key={header}>{header}</Table.Th>
            ))}
          </Table.Tr>
        </Table.Thead>
        <Table.Tbody>
          {rows.map((row) => (
            <Table.Tr key={row.join("-")}>
              {row.map((cell) => (
                <Table.Td key={cell}>{cell}</Table.Td>
              ))}
            </Table.Tr>
          ))}
        </Table.Tbody>
      </Table>
    </Box>
  );
}

export default ApushScoreCalculatorPage;
